import { Helmet } from "react-helmet-async";

const SITE_URL = "https://email2contact.com";
const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/AnGrvYA81FbifMppvz6lMBwnZ7u1/social-images/social-1762098604385-Email2Contact_2.png";

/**
 * Routes with a dedicated 1200×630 OG image at `public/og/<slug>.png`.
 * To enable a per-route image:
 *   1. Drop a 1200×630 PNG into `public/og/` named after the slug below.
 *   2. The SEO component automatically picks it up — no code change in pages needed.
 * If no file exists yet, the global DEFAULT_OG_IMAGE is used as fallback.
 */
const ROUTE_OG_SLUGS: Record<string, string> = {
  "/": "home",
  "/plans": "plans",
  "/support": "support",
  "/privacy": "privacy",
  "/terms": "terms",
  "/refund-policy": "refund-policy",
  "/impressum": "impressum",
};

const resolveOgImage = (path: string, override?: string): string => {
  if (override) return override;
  const slug = ROUTE_OG_SLUGS[path];
  if (slug) return `${SITE_URL}/og/${slug}.png`;
  return DEFAULT_OG_IMAGE;
};

interface SEOProps {
  title: string;
  description: string;
  /** Path beginning with "/" — used to build canonical URL */
  path?: string;
  /** Optional override for the OG / Twitter image. Falls back to per-route asset, then global default. */
  image?: string;
  /** One or more JSON-LD schema objects to embed in the head */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Defaults to "index, follow, max-image-preview:large, max-snippet:-1" */
  robots?: string;
  /** Set to true on legal/secondary pages where indexing is fine but lower priority */
  noindex?: boolean;
}

/**
 * Per-route SEO component. Sets title, meta description, canonical, OpenGraph,
 * Twitter Card and optional JSON-LD structured data.
 *
 * OG image resolution order:
 *   1. Explicit `image` prop
 *   2. `public/og/<slug>.png` if route is registered in ROUTE_OG_SLUGS
 *   3. Global DEFAULT_OG_IMAGE
 */
export const SEO = ({
  title,
  description,
  path = "/",
  image,
  jsonLd,
  robots,
  noindex,
}: SEOProps) => {
  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  const ogImage = resolveOgImage(path, image);
  const robotsContent =
    robots ||
    (noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1");

  const schemas = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Email2Contact" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

/** Helper builders for common JSON-LD blocks */
export const buildBreadcrumbSchema = (
  items: { name: string; path: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${SITE_URL}${item.path === "/" ? "" : item.path}`,
  })),
});

export const buildFAQSchema = (
  faqs: { question: string; answer: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.answer,
    },
  })),
});
